'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef } from 'react';
import { useSession } from '@/lib/auth-client';
import { useAddAttendance, useRemoveAttendance } from '@/lib/hooks/useAttendance';
import { useObjectActivities } from '@/lib/hooks/useObjectActivities';
import { useEngagementSummary } from '@/lib/hooks/useEngagementSummary';
import { CalendarCheck } from 'lucide-react';
import clsx from 'clsx';
import { toast } from 'sonner';
import { useSignInModal } from '@/lib/ui';
import type { AttendanceSettings } from '@sizzlenz/shared';
interface AttendButtonProps {
  objectId: string;
  objectType: 'initiative' | 'entity' | 'item' | 'collection' | 'content';
  attendanceSettings: AttendanceSettings;
  attendanceCount?: number;
  /** When true, shows the count inside the button instead of as a separate label */
  countInline?: boolean;
  /** When true, renders the button in a disabled state */
  disabled?: boolean;
}
export function AttendButton(t0) {
  const $ = _c(51);
  const {
    objectId,
    objectType,
    attendanceSettings,
    attendanceCount,
    countInline: t1,
    disabled: t2
  } = t0;
  const countInline = t1 === undefined ? false : t1;
  const disabled = t2 === undefined ? false : t2;
  const {
    data: session
  } = useSession();
  let t3;
  if ($[0] !== session?.user?.id) {
    t3 = session?.user?.id?.toString();
    $[0] = session?.user?.id;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const currentUserId = t3;
  const {
    showSignInModal
  } = useSignInModal();
  const pendingActionRef = useRef(false);
  const {
    data: t4
  } = useObjectActivities(objectId, objectType);
  let t5;
  if ($[2] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const activities = t5;
  const {
    data: engagementData
  } = useEngagementSummary(objectId, objectType);
  const addAttendance = useAddAttendance();
  const removeAttendance = useRemoveAttendance();
  let t6;
  if ($[4] !== activities || $[5] !== currentUserId) {
    let t7;
    if ($[7] !== currentUserId) {
      t7 = a => a.type === "attendance" && String(a.actorId) === currentUserId && !a.deletedAt;
      $[7] = currentUserId;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    t6 = activities.find(t7);
    $[4] = activities;
    $[5] = currentUserId;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const existingAttendance = t6;
  const isAttending = !!existingAttendance;
  const attendanceStatus = (existingAttendance?.data as any)?.status || null;
  const currentCount = engagementData?.engagement?.attendance?.registered?.count ?? attendanceCount ?? 0;
  const capacity = attendanceSettings.capacity;
  const isFull = capacity != null && currentCount >= capacity;
  const spotsRemaining = capacity != null ? Math.max(0, capacity - currentCount) : null;
  let t7;
  if ($[9] !== addAttendance || $[10] !== attendanceSettings.requiresApproval || $[11] !== currentUserId || $[12] !== isAttending || $[13] !== isFull || $[14] !== objectId || $[15] !== objectType || $[16] !== removeAttendance || $[17] !== showSignInModal) {
    t7 = async () => {
      if (!currentUserId) {
        pendingActionRef.current = true;
        showSignInModal({
          description: <div className="font-bold p-6"><div className="text-lg">Sign in to register attendance</div></div>,
          preferFullscreenForNewUsers: false,
          onSuccess: () => {
            if (!pendingActionRef.current) {
              return;
            }
            pendingActionRef.current = false;
            addAttendance.mutate({
              objectId,
              objectType
            }, {
              onSuccess: () => {
                toast.success(attendanceSettings.requiresApproval ? "Attendance requested \u2013 awaiting approval" : "Attendance registered");
              }
            });
          }
        });
        return;
      }
      if (isAttending) {
        await removeAttendance.mutateAsync({
          actorId: currentUserId,
          objectId,
          objectType
        });
        toast.success("Attendance cancelled");
      } else {
        if (isFull) {
          toast.error("This event is at capacity");
          return;
        }
        await addAttendance.mutateAsync({
          objectId,
          objectType
        }, {
          onSuccess: () => {
            toast.success(attendanceSettings.requiresApproval ? "Attendance requested \u2013 awaiting approval" : "Attendance registered");
          }
        });
      }
    };
    $[9] = addAttendance;
    $[10] = attendanceSettings.requiresApproval;
    $[11] = currentUserId;
    $[12] = isAttending;
    $[13] = isFull;
    $[14] = objectId;
    $[15] = objectType;
    $[16] = removeAttendance;
    $[17] = showSignInModal;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleToggle = t7;
  if (!session?.user && disabled) {
    return null;
  }
  const isPending = addAttendance.isPending || removeAttendance.isPending;
  const isDisabled = disabled && !!session?.user || isPending || isFull && !isAttending;
  const displayCount = engagementData?.engagement?.attendance?.registered?.count ?? attendanceCount ?? 0;
  const buttonLabel = isAttending ? attendanceStatus === "pending_approval" ? "Pending Approval" : "Attending" : isFull ? "Full" : "Attend";
  const t8 = isAttending ? "bg-emerald-50 text-emerald-700 border border-emerald-300" : isFull ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed" : "bg-gray-100 text-gray-900 border border-gray-200 hover:bg-gray-150";
  const t9 = isDisabled && "opacity-50 cursor-not-allowed";
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = clsx("flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer", t8, t9);
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = isAttending && "fill-emerald-200";
  let t12;
  if ($[22] !== t11) {
    t12 = clsx("w-5 h-5", t11);
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = isAttending ? 2.5 : 2;
  let t14;
  if ($[24] !== t12 || $[25] !== t13) {
    t14 = <CalendarCheck className={t12} strokeWidth={t13} />;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== buttonLabel) {
    t15 = <span>{buttonLabel}</span>;
    $[27] = buttonLabel;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== countInline || $[30] !== displayCount) {
    t16 = countInline && displayCount > 0 && <span className="bg-emerald-100 text-emerald-800 text-xs font-semibold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">{displayCount}</span>;
    $[29] = countInline;
    $[30] = displayCount;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== handleToggle || $[33] !== isDisabled || $[34] !== t10 || $[35] !== t14 || $[36] !== t15 || $[37] !== t16) {
    t17 = <button onClick={handleToggle} disabled={isDisabled} className={t10}>{t14}{t15}{t16}</button>;
    $[32] = handleToggle;
    $[33] = isDisabled;
    $[34] = t10;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== countInline || $[40] !== displayCount) {
    t18 = !countInline && displayCount > 0 && <span className="text-sm text-gray-500">{displayCount} attending</span>;
    $[39] = countInline;
    $[40] = displayCount;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t17 || $[43] !== t18) {
    t19 = <div className="flex items-center gap-2">{t17}{t18}</div>;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== isAttending || $[46] !== spotsRemaining) {
    t20 = spotsRemaining != null && spotsRemaining > 0 && spotsRemaining <= 10 && !isAttending && <p className="text-xs text-orange-600 font-medium">{spotsRemaining} spot{spotsRemaining !== 1 ? "s" : ""} remaining</p>;
    $[45] = isAttending;
    $[46] = spotsRemaining;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t19 || $[49] !== t20) {
    t21 = <div className="space-y-1">{t19}{t20}</div>;
    $[48] = t19;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  return t21;
}